import { useMemo } from "react";

import { useAtomValue } from "jotai";
import { IInitChatBotDocuments } from "library/types";
import { extractFileNameFromUrl } from "library/utils";
import { selectedClientAtom } from "store";

import { useBenefitsData } from "./useBenefitsData";
import useEmployee from "./useEmployee";

export function useAllUserDocumentsForChatbotNew(): IInitChatBotDocuments[] | null {
  const selectedClient = useAtomValue(selectedClientAtom);
  const { employeeData, employeeDataLoading } = useEmployee();
  const {
    enrolledFilteredData,
    eligibleFilteredData,
    isLoading: isLoadingBenefitsData,
  } = useBenefitsData();

  const allDocuments = useMemo(() => {
    const documents = [] as {
      PackageName?: string;
      DocumentPath: string;
      LastModifiedAt: string;
      Users: { Id: string; Name: string }[];
    }[];

    if (!employeeData || isLoadingBenefitsData) return null;

    [...enrolledFilteredData, ...eligibleFilteredData].forEach((enrollment) => {
      const enrollmentDocuments = enrollment?.attachments || [];

      enrollmentDocuments.forEach((document) => {
        const enrollmentUserId = enrollment?.isDependent
          ? enrollment?.employeeDependentTag?.toString()
          : enrollment?.employeeLoginEmailAddress;

        const enrollmentUserName = enrollment?.isDependent
          ? employeeData?.dependents?.find(
              (dependent) => dependent?.employeeDependentTag?.toString() === enrollmentUserId
            )?.firstName
          : employeeData?.firstName;

        if (!enrollmentUserId || !enrollmentUserName) return;

        const documentIndex = documents.findIndex(
          (doc) =>
            extractFileNameFromUrl(doc.DocumentPath) ===
            extractFileNameFromUrl(document.attachmentPath)
        );

        if (documentIndex !== -1) {
          // Document exists, check if user is already added
          const userExists = documents[documentIndex].Users.some(
            (user) => user.Id === enrollmentUserId
          );

          if (!userExists) {
            // Push new user if they are not already in the list
            documents[documentIndex].Users.push({
              Id: enrollmentUserId,
              Name: enrollmentUserName,
            });
          }
        } else {
          // Document doesn't exist, create new entry
          const packageName = enrollment?.name || "";
          documents.push({
            DocumentPath: document.attachmentPath || "",
            LastModifiedAt: document.lastModifiedAt || "",
          ...(packageName ? { PackageName: packageName } : {}),

            Users: [
              {
                Id: enrollmentUserId,
                Name: enrollmentUserName,
              },
            ],
          });
        }
      });
    });

    //  selectedClient attachments
    (selectedClient?.attachments || []).forEach((attachment) => {
      const attachmentPath = attachment.attachmentPath || "";
      const fileName = extractFileNameFromUrl(attachmentPath);

      const documentExists = documents.some(
        (doc) => extractFileNameFromUrl(doc.DocumentPath) === fileName
      );

      if (!documentExists) {
        documents.push({
          DocumentPath: attachmentPath,
          LastModifiedAt: attachment.lastModifiedAt || "",
          Users: [
            {
              Id: employeeData?.employeeLoginEmailAddress || "",
              Name: employeeData?.firstName || "",
            },
            ...(employeeData.dependents?.length
              ? employeeData?.dependents?.map((dependent) => {
                  return {
                    Id: dependent?.employeeDependentTag?.toString() || "",
                    Name: dependent?.firstName || "",
                  };
                }) || []
              : []),
          ],
        });
      }
    });

    //  employee attachments

    (employeeData?.attachments || []).forEach((attachment) => {
      const attachmentPath = attachment.attachmentPath || "";
      const fileName = extractFileNameFromUrl(attachmentPath);

      const documentExists = documents.some(
        (doc) => extractFileNameFromUrl(doc.DocumentPath) === fileName
      );

      if (!documentExists) {
        documents.push({
          DocumentPath: attachmentPath,
          LastModifiedAt: attachment.lastModifiedAt || "",
          Users: [
            {
              Id: employeeData?.employeeLoginEmailAddress || "",
              Name: employeeData?.firstName || "",
            },
          ],
        });
      }
    });

    return documents;
  }, [employeeData, isLoadingBenefitsData, selectedClient]);

  if (isLoadingBenefitsData || employeeDataLoading) return null;

  return allDocuments;
}
